import { skipToken, useQuery } from '@tanstack/react-query';
import {
  AdvertiserInfo,
  BrandHealthEntry,
  BrandHealthInfo,
  BrandHealthInfoParams
} from '@blisspointmedia/bpm-types/dist/BrandHealthMetrics';
import { MetricsLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { formatBrandHealthEntries } from './brand-health-metrics-utils';

/** The chart filters. The lambda resolves the advertisers and the date range from `cid`. */
export type BrandHealthChartFilters = Omit<
  BrandHealthInfoParams,
  'focalAdvertiser' | 'nonFocalAdvertisers' | 'minDate' | 'maxDate'
>;

const fetchAdvertiserInfo = async (cid: string): Promise<AdvertiserInfo> => {
  const res = await MetricsLambdaFetch('/getAdvertiserInfo', { params: { cid } });
  return awaitJSON<AdvertiserInfo>(res);
};

const fetchBrandHealthChartData = async (
  cid: string,
  filters: BrandHealthChartFilters
): Promise<BrandHealthEntry[]> => {
  const res = await MetricsLambdaFetch('/getBrandHealthInfo', { params: { cid, ...filters } });
  const parsedRes = await awaitJSON<BrandHealthInfo>(res);
  return formatBrandHealthEntries(parsedRes.data, filters.dateInterval);
};

// No retry on either query: a failure is usually the DuckDB API's ~15s deadline, and a retry
// doubles that wait before the error shows. The page reports errors itself, through setError.
export const useBrandHealthAdvertiserInfo = (
  cid: string,
  enabled: boolean
): ReturnType<typeof useQuery<AdvertiserInfo>> =>
  useQuery({
    queryKey: ['brandHealthAdvertiserInfo', cid],
    queryFn: () => fetchAdvertiserInfo(cid),
    enabled: enabled && Boolean(cid),
    retry: false,
    meta: { suppressGlobalError: true }
  });

/**
 * Independent of useBrandHealthAdvertiserInfo, so the page fires both requests together instead
 * of waiting for the advertiser info before it asks for the chart.
 */
export const useBrandHealthChartData = (
  cid: string,
  filters: BrandHealthChartFilters | undefined,
  enabled: boolean
): ReturnType<typeof useQuery<BrandHealthEntry[]>> =>
  useQuery({
    queryKey: ['brandHealthChartData', cid, filters],
    queryFn: filters ? () => fetchBrandHealthChartData(cid, filters) : skipToken,
    enabled: enabled && Boolean(cid),
    retry: false,
    meta: { suppressGlobalError: true }
  });
